import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { UserPlus, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const TopNavbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userRole } = useAuth();
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleOpenAIChat = (question?: string) => {
    // Replace with real chat integration
    console.log('Open AI Chat with:', question);
  };

  // Helper function to handle routing with mobile race condition fix
  const handleUserRouting = () => {
    if (!user) return;

    console.log('TopNavbar: Routing user', {
      userId: user.id,
      userRole,
      is_superuser: user.user_metadata?.is_superuser,
      user_metadata_role: user.user_metadata?.role
    });

    // Check if this might be a mobile race condition (userRole not loaded yet)
    const isMobileRaceCondition = !userRole && user.user_metadata?.role;
    
    if (isMobileRaceCondition) {
      console.log('TopNavbar: Detected mobile race condition, using metadata role');
      // Use role from user metadata as fallback
      const metadataRole = user.user_metadata?.role;
      if (user.user_metadata?.is_superuser || metadataRole === 'superadmin') {
        navigate("/superadmin");
      } else if (metadataRole === 'parent') {
        navigate("/parent-portal");
      } else {
        navigate("/dashboard");
      }
    } else {
      // Normal routing logic
      if (user.user_metadata?.is_superuser || userRole === 'superadmin') {
        navigate("/superadmin");
      } else if (userRole === 'parent') {
        navigate("/parent-portal");
      } else {
        navigate("/dashboard");
      }
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (mobileMenuOpen && !target.closest('[data-mobile-menu]')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);
  
  console.log("TopNavbar rendering with auth state:", {
    user: !!user,
    userRole
  });

  // Helper function to check if a path is active
  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      <style>{`
        /* Navigation link hover effects */
        .nav-link {
          position: relative;
          transition: color 0.3s ease;
        }
        
        .nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -4px;
          left: 50%;
          background-color: #FA8C00;
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }
        
        .nav-link:hover::after {
          width: 100%;
        }
        
        /* Active page styles */
        .nav-link.active {
          color: #FA8C00;
          font-weight: 600;
        }
        
        .nav-link.active::after {
          width: 100%;
        }
        
        /* Logo hover effect */
        .logo-hover {
          transition: transform 0.2s ease;
        }
        
        .logo-hover:hover {
          transform: scale(1.05);
        }
        
        /* Enhanced button styles */
        .btn-enhanced {
          transition: all 0.2s ease-in-out;
        }
        
        .btn-enhanced:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(250, 140, 0, 0.25);
        }
        
        .btn-enhanced:active {
          transform: scale(0.98);
        }
        
        /* Mobile menu animation */
        .mobile-menu-enter {
          animation: slideInFromTop 0.3s ease-out;
        }
        
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <nav className="bg-white border-b border-gray-200 fixed w-full top-0 left-0 z-30 shadow-sm">
        <div className="container mx-auto px-4 bg-transparent">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <div className="flex items-center">
              <button onClick={() => navigate("/")} className="flex items-center logo-hover">
                <img 
                  alt="Hivetivity Logo" 
                  src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
                  className="h-12 w-auto object-contain" 
                />
              </button>
            </div>

            {/* Middle nav links - show on medium screens and up */}
            <div className="hidden md:flex flex-1 items-center justify-center space-x-8">
              <button 
                onClick={() => navigate("/features")} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg ${isActivePath("/features") ? "active" : ""}`}
              >
                Features
              </button>
              <button 
                onClick={() => navigate("/pricing")} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg ${isActivePath("/pricing") ? "active" : ""}`}
              >
                Pricing
              </button>
              <button 
                onClick={() => navigate("/blog")} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg ${isActivePath("/blog") ? "active" : ""}`}
              >
                Blog
              </button>
              <button 
                onClick={() => navigate("/whats-new")} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg ${isActivePath("/whats-new") ? "active" : ""}`}
              >
                What's New
              </button>
              <button 
                onClick={() => navigate("/our-why")} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg ${isActivePath("/our-why") ? "active" : ""}`}
              >
                Our Why
              </button>
              <button 
                onClick={() => navigate("/contact")} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg ${isActivePath("/contact") ? "active" : ""}`}
              >
                Contact
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden" data-mobile-menu>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle mobile menu"
                data-mobile-menu
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>

            {/* Right side - help and auth buttons */}
            <div className="hidden md:flex items-center space-x-2 sm:space-x-4">
              {user ? (
                <Button 
                  variant="outline"
                  onClick={handleUserRouting} 
                  className="btn-enhanced border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold text-sm sm:text-lg px-2 sm:px-4"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      // Always redirect to app domain for auth
                      const appUrl = import.meta.env.DEV 
                        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
                        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
                      window.location.href = appUrl + '/auth';
                    }} 
                    className="btn-enhanced border-primary text-primary hover:bg-primary hover:text-primary-foreground text-sm sm:text-lg font-semibold px-2 sm:px-4"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate("/business-trial")} 
                    className="btn-enhanced bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm text-sm sm:text-lg font-medium px-2 sm:px-4"
                  >
                    <UserPlus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Try Free</span>
                    <span className="sm:hidden">Try</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-20 mobile-menu-enter" data-mobile-menu>
          <div className="container mx-auto px-4 py-4">
            {/* Navigation Links */}
            <div className="flex flex-col space-y-4 mb-6">
              <button 
                onClick={() => {
                  navigate("/features");
                  setMobileMenuOpen(false);
                }} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg text-left py-2 ${isActivePath("/features") ? "active" : ""}`}
              >
                Features
              </button>
              <button 
                onClick={() => {
                  navigate("/pricing");
                  setMobileMenuOpen(false);
                }} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg text-left py-2 ${isActivePath("/pricing") ? "active" : ""}`}
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  navigate("/blog");
                  setMobileMenuOpen(false);
                }} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg text-left py-2 ${isActivePath("/blog") ? "active" : ""}`}
              >
                Blog
              </button>
              <button 
                onClick={() => {
                  navigate("/whats-new");
                  setMobileMenuOpen(false);
                }} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg text-left py-2 ${isActivePath("/whats-new") ? "active" : ""}`}
              >
                What's New
              </button>
              <button 
                onClick={() => {
                  navigate("/our-why");
                  setMobileMenuOpen(false);
                }} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg text-left py-2 ${isActivePath("/our-why") ? "active" : ""}`}
              >
                Our Why
              </button>
              <button 
                onClick={() => {
                  navigate("/contact");
                  setMobileMenuOpen(false);
                }} 
                className={`nav-link text-secondary hover:text-primary font-medium text-lg text-left py-2 ${isActivePath("/contact") ? "active" : ""}`}
              >
                Contact
              </button>
            </div>

            {/* Auth Buttons */}
            <div className="flex flex-col space-y-3">
              {user ? (
                <Button 
                  variant="outline"
                  onClick={() => {
                    handleUserRouting();
                    setMobileMenuOpen(false);
                  }} 
                  className="btn-enhanced border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold text-lg w-full py-3"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      // Always redirect to app domain for auth
                      const appUrl = import.meta.env.DEV 
                        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
                        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
                      window.location.href = appUrl + '/auth';
                      setMobileMenuOpen(false);
                    }} 
                    className="btn-enhanced border-primary text-primary hover:bg-primary hover:text-primary-foreground text-lg font-semibold w-full py-3"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => {
                      navigate("/business-trial");
                      setMobileMenuOpen(false);
                    }} 
                    className="btn-enhanced bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm text-lg font-medium w-full py-3"
                  >
                    <UserPlus className="mr-2 h-5 w-5" />
                    Try Free
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onboarding modal will be implemented here */}
    </>
  );
};

export default TopNavbar;
